import React, { useState, useEffect, useCallback, useRef, useMemo } from "react";
import { Link as RouterLink, useLocation, useNavigate } from "react-router-dom";
import {
    Box, Flex, Text, Button, IconButton, Input, Select, HStack, VStack,
    SimpleGrid, Badge, Tooltip, Spinner, useToast, Table, Thead, Tbody, Tr, Th, Td,
    Tabs, TabList, Tab, TabPanels, TabPanel,
    Modal, ModalOverlay, ModalContent, ModalHeader, ModalCloseButton, ModalBody, ModalFooter,
    FormControl, FormLabel, Switch, Textarea, NumberInput, NumberInputField,
    Drawer, DrawerOverlay, DrawerContent, DrawerHeader, DrawerCloseButton, DrawerBody,
    Divider, Progress, useDisclosure, Avatar,
    Skeleton, Menu, MenuButton, MenuList, MenuItem,
    AlertDialog, AlertDialogOverlay, AlertDialogContent, AlertDialogHeader, AlertDialogBody, AlertDialogFooter
} from "@chakra-ui/react";
import {
    Trophy, Users, BarChart3, Settings, Calendar, RefreshCw, Plus, Pencil, Trash2,
    Eye, ChevronRight, Clock, Sparkles, PlayCircle,
    ChevronLeft, AlertCircle, CheckCircle2,
    FileText, Zap, Image as ImageIcon,
    Building2, Wrench, Phone
} from "lucide-react";
import {
    BarChart, Bar, XAxis, YAxis, CartesianGrid,
    Tooltip as RechartsTooltip, ResponsiveContainer, Cell
} from "recharts";
import {
    getDesempenhoDashboard, getDesempenhoAtendentes, createDesempenhoAtendente,
    updateDesempenhoAtendente, deleteDesempenhoAtendente, getDesempenhoPoliUsers,
    getDesempenhoDepartamentosConfigs, updateDesempenhoDepartamento,
    getDesempenhoTarefasTemplates, createDesempenhoTarefaTemplate,
    updateDesempenhoTarefaTemplate, deleteDesempenhoTarefaTemplate,
    getDesempenhoAvaliacoes, getDesempenhoAvaliacao, rodarDesempenhoAvaliacoes,
    sincronizarDesempenho, getDesempenhoRamalInternoPresencaRealtime, getUsuarios
} from "../services/api";
import { semBloqueados } from "../hooks/useUsuarios";

// ===== helpers =====
const DEP_BADGE = {
    comercial:  { label: "Comercial",  color: "orange" },
    suporte:    { label: "Suporte",    color: "blue" },
    financeiro: { label: "Financeiro", color: "purple" },
};

// Indicadores do gerente de contas (modo CRM). A ordem segue os pesos padrão.
const CRM_INDICADORES = [
    { codigo: "crm_ligacoes_realizadas", label: "Ligações realizadas", abbr: "Lig" },
    { codigo: "crm_mensagens_whatsapp",  label: "Mensagens WhatsApp",   abbr: "Msg" },
    { codigo: "crm_clientes_contatados", label: "Clientes contatados",  abbr: "Cli" },
    { codigo: "crm_tarefas_concluidas",  label: "Tarefas concluídas",   abbr: "Tar" },
];

const crmIndCor = (nota) => nota == null ? "gray" : nota >= 100 ? "green" : nota >= 60 ? "teal" : nota >= 30 ? "yellow" : "red";

// Célula do ranking para atendentes em modo CRM: ocupa o espaço das colunas POLI
// (T. Resp / SLA / Contatos / Fechados / ML) com os indicadores do CRM (realizado/meta).
const CrmRankingCells = ({ tarefas }) => {
    const byCodigo = {};
    (tarefas || []).forEach(t => { if (t.template?.codigo) byCodigo[t.template.codigo] = t; });
    return (
        <Td colSpan={5}>
            <HStack spacing={2} flexWrap="wrap">
                {CRM_INDICADORES.map(ind => {
                    const t = byCodigo[ind.codigo];
                    const real = t?.valor_realizado != null ? Math.round(Number(t.valor_realizado)) : 0;
                    const meta = t?.valor_esperado != null ? Math.round(Number(t.valor_esperado)) : null;
                    const nota = t?.nota != null ? Number(t.nota) : null;
                    return (
                        <Tooltip key={ind.codigo} label={ind.label} hasArrow>
                            <Badge colorScheme={crmIndCor(nota)} variant="subtle" fontSize="11px" borderRadius="md" px={2} py="2px">
                                {ind.abbr} {real}{meta != null ? `/${meta}` : ""}
                            </Badge>
                        </Tooltip>
                    );
                })}
            </HStack>
        </Td>
    );
};

const CAT_RESOLUCAO = {
    venda:              { label: "Venda",             color: "green"  },
    transferencia:      { label: "Transferência",     color: "blue"   },
    sem_peca:           { label: "Sem Peça",          color: "yellow" },
    em_andamento:       { label: "Em Andamento",      color: "purple" },
    abandonado_cliente: { label: "Abandonado",        color: "red"    },
};

const STATUS_AVAL = {
    pendente:    { label: "Pendente",    color: "gray"    },
    processando: { label: "Processando", color: "blue"    },
    concluida:   { label: "Concluída",   color: "green"   },
    erro:        { label: "Erro",        color: "red"     },
};

const ontemISO = () => {
    const d = new Date();
    d.setDate(d.getDate() - 1);
    return d.toISOString().slice(0, 10);
};

const hojeMinusISO = (dias) => {
    const d = new Date();
    d.setDate(d.getDate() - dias);
    return d.toISOString().slice(0, 10);
};

const PERIODO_PRESETS = {
    "30d": { label: "Últimos 30 dias", dias: 30 },
    "3m":  { label: "Últimos 3 meses", dias: 90 },
    "1a":  { label: "Último ano",      dias: 365 },
    "custom": { label: "Personalizado", dias: null },
};

const rangeFromPreset = (preset) => {
    if (preset === "custom") return null;
    const cfg = PERIODO_PRESETS[preset];
    if (!cfg) return null;
    return { inicio: hojeMinusISO(cfg.dias), fim: hojeMinusISO(0) };
};

const PeriodoFiltro = ({ preset, inicio, fim, onPresetChange, onInicioChange, onFimChange }) => (
    <HStack spacing={2} flexWrap="wrap">
        <Box>
            <Text fontSize="10px" color="gray.500" fontWeight="semibold" textTransform="uppercase" mb={1}>Período</Text>
            <Select size="sm" bg="white" borderRadius="md" value={preset} onChange={(e) => onPresetChange(e.target.value)} w="180px">
                {Object.entries(PERIODO_PRESETS).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
            </Select>
        </Box>
        {preset === "custom" && (
            <>
                <Box>
                    <Text fontSize="10px" color="gray.500" fontWeight="semibold" textTransform="uppercase" mb={1}>Início</Text>
                    <Input size="sm" type="date" bg="white" borderRadius="md" value={inicio} onChange={(e) => onInicioChange(e.target.value)} w="150px" />
                </Box>
                <Box>
                    <Text fontSize="10px" color="gray.500" fontWeight="semibold" textTransform="uppercase" mb={1}>Fim</Text>
                    <Input size="sm" type="date" bg="white" borderRadius="md" value={fim} onChange={(e) => onFimChange(e.target.value)} w="150px" />
                </Box>
            </>
        )}
    </HStack>
);

const formatDate = (iso) => {
    if (!iso) return "—";
    try {
        const d = iso.length === 10 ? new Date(iso + "T12:00:00") : new Date(iso);
        return d.toLocaleDateString("pt-BR");
    } catch { return iso; }
};

const formatSeg = (seg) => {
    const v = Number(seg || 0);
    if (v < 60) return `${v.toFixed(0)}s`;
    if (v < 3600) return `${(v / 60).toFixed(1)} min`;
    return `${(v / 3600).toFixed(1)}h`;
};

const notaColor = (nota) => {
    const n = Number(nota || 0);
    if (n >= 85) return "green";
    if (n >= 70) return "blue";
    if (n >= 50) return "yellow";
    return "red";
};

const ContentShell = ({ children }) => (
    <Box ml={{ base: "0", md: "64px" }} p={{ base: 3, md: 6 }} pt="75px" minH="100vh" bg="gray.50">
        <Box maxW="1600px" mx="auto">{children}</Box>
    </Box>
);

// ===== DASHBOARD TAB =====
const DashboardTab = ({ onOpenSync, onOpenRamal }) => {
    const toast = useToast();
    const [loading, setLoading] = useState(true);
    const [preset, setPreset] = useState("30d");
    const [inicio, setInicio] = useState(() => rangeFromPreset("30d").inicio);
    const [fim, setFim] = useState(() => rangeFromPreset("30d").fim);
    const [departamento, setDepartamento] = useState("");
    const [dashboard, setDashboard] = useState(null);

    const handlePresetChange = (p) => {
        setPreset(p);
        if (p !== "custom") {
            const r = rangeFromPreset(p);
            if (r) { setInicio(r.inicio); setFim(r.fim); }
        }
    };

    const fetch = useCallback(async () => {
        if (!inicio || !fim) return;
        setLoading(true);
        const params = { inicio, fim };
        if (departamento) params.departamento = departamento;
        const res = await getDesempenhoDashboard(params);
        if (res) setDashboard(res);
        else toast({ status: "error", title: "Erro ao carregar dashboard" });
        setLoading(false);
    }, [inicio, fim, departamento, toast]);

    useEffect(() => { fetch(); }, [fetch]);

    const agregado = dashboard?.agregado || {};
    const atendentes = (dashboard?.atendentes || []).slice().sort((a, b) => (b.metrica?.nota_geral || 0) - (a.metrica?.nota_geral || 0));
    const periodoLabel = `${formatDate(dashboard?.inicio || inicio)} – ${formatDate(dashboard?.fim || fim)}`;

    return (
        <Box>
            {/* Header / Filtros */}
            <Flex justify="space-between" align="center" mb={5} flexWrap="wrap" gap={3}>
                <HStack spacing={3} flexWrap="wrap" align="flex-end">
                    <PeriodoFiltro
                        preset={preset}
                        inicio={inicio}
                        fim={fim}
                        onPresetChange={handlePresetChange}
                        onInicioChange={setInicio}
                        onFimChange={setFim}
                    />
                    <Box>
                        <Text fontSize="10px" color="gray.500" fontWeight="semibold" textTransform="uppercase" mb={1}>Departamento</Text>
                        <Select size="sm" bg="white" borderRadius="md" value={departamento} onChange={(e) => setDepartamento(e.target.value)} w="170px">
                            <option value="">Todos</option>
                            <option value="comercial">Comercial</option>
                            <option value="suporte">Suporte</option>
                            <option value="financeiro">Financeiro</option>
                        </Select>
                    </Box>
                </HStack>
                <HStack>
                    <Tooltip label="Atualizar"><IconButton icon={<RefreshCw size={15} />} size="sm" onClick={fetch} aria-label="Atualizar" variant="outline" /></Tooltip>
                    <Button size="sm" leftIcon={<Phone size={15} />} colorScheme="blue" variant="outline" onClick={onOpenRamal}>Verificar presença ramal</Button>
                    <Button size="sm" leftIcon={<PlayCircle size={15} />} colorScheme="orange" onClick={onOpenSync}>Sincronizar</Button>
                </HStack>
            </Flex>

            {/* Cards agregados */}
            <SimpleGrid columns={{ base: 1, sm: 2, lg: 4 }} spacing={4} mb={5}>
                <MetricCard label="Atendentes Ativos" value={agregado.total_atendentes ?? "—"} icon={Users} color="blue" loading={loading} />
                <MetricCard label="Nota Média Geral" value={agregado.nota_media != null ? `${agregado.nota_media.toFixed(1)}` : "—"} icon={Trophy} color={notaColor(agregado.nota_media)} loading={loading} />
                <MetricCard label="Avaliações IA" value={agregado.total_avaliacoes_ia ?? "—"} icon={Sparkles} color="purple" loading={loading} />
                <MetricCard label="Período" value={periodoLabel} icon={Calendar} color="gray" loading={loading} isText />
            </SimpleGrid>

            {/* Tabela Ranking */}
            <Box bg="white" borderRadius="xl" boxShadow="sm" border="1px solid" borderColor="gray.200" overflow="hidden">
                <Flex px={4} py={3} borderBottom="1px solid" borderColor="gray.100" align="center" justify="space-between">
                    <HStack>
                        <Trophy size={16} color="#F68633" />
                        <Text fontSize="sm" fontWeight="bold" color="gray.700">Ranking de Atendentes</Text>
                    </HStack>
                    <Text fontSize="xs" color="gray.500">{atendentes.length} atendente(s)</Text>
                </Flex>
                {loading ? (
                    <Box p={5}><Skeleton height="40px" mb={2} /><Skeleton height="40px" mb={2} /><Skeleton height="40px" mb={2} /></Box>
                ) : atendentes.length === 0 ? (
                    <Box p={8} textAlign="center">
                        <Text fontSize="sm" color="gray.400">Nenhum atendente encontrado para os filtros selecionados.</Text>
                        <Text fontSize="xs" color="gray.400" mt={1}>O cron rodou para este dia?</Text>
                    </Box>
                ) : (
                    <Box overflowX="auto">
                        <Table size="sm" variant="simple">
                            <Thead bg="gray.50">
                                <Tr>
                                    <Th fontSize="xs" color="gray.500">#</Th>
                                    <Th fontSize="xs" color="gray.500">Atendente</Th>
                                    <Th fontSize="xs" color="gray.500">Depto</Th>
                                    <Th fontSize="xs" color="gray.500" isNumeric>Nota</Th>
                                    <Th fontSize="xs" color="gray.500" isNumeric>T. Resp</Th>
                                    <Th fontSize="xs" color="gray.500" isNumeric>SLA %</Th>
                                    <Th fontSize="xs" color="gray.500" isNumeric>Contatos</Th>
                                    <Th fontSize="xs" color="gray.500" isNumeric>Fechados</Th>
                                    <Th fontSize="xs" color="gray.500" isNumeric>ML</Th>
                                    <Th fontSize="xs" color="gray.500" textAlign="center">Ações</Th>
                                </Tr>
                            </Thead>
                            <Tbody>
                                {atendentes.map((item, idx) => {
                                    const a = item.atendente || {};
                                    const m = item.metrica || {};
                                    const sla = (a.departamento && dashboard?.sla_por_departamento?.[a.departamento]) || 5;
                                    const slaResp = (item.tarefas || []).find(t => t.template?.codigo === "sla_resposta");
                                    const pctSLA = slaResp?.detalhes?.pct_aderencia;
                                    const tempoResp = m.tempo_medio_primeira_resposta_seg;
                                    const dep = DEP_BADGE[a.departamento] || { label: a.departamento, color: "gray" };
                                    const slaMin = slaResp?.detalhes?.sla_minutos || sla;
                                    const tempoAlto = tempoResp && tempoResp > slaMin * 60;
                                    return (
                                        <Tr key={a.id} _hover={{ bg: "gray.50" }}>
                                            <Td>
                                                <Badge colorScheme={idx === 0 ? "yellow" : idx === 1 ? "gray" : idx === 2 ? "orange" : "blackAlpha"} fontSize="10px" borderRadius="md">
                                                    {idx + 1}º
                                                </Badge>
                                            </Td>
                                            <Td>
                                                <HStack>
                                                    <Avatar size="xs" name={a.user?.name || a.apelido_poli} />
                                                    <Box minW={0}>
                                                        <Text fontSize="sm" fontWeight="semibold" color="gray.800" noOfLines={1}>{a.user?.name || a.apelido_poli || "—"}</Text>
                                                        <Text fontSize="10px" color="gray.500" noOfLines={1}>{a.apelido_poli || a.user?.email || "—"}</Text>
                                                    </Box>
                                                </HStack>
                                            </Td>
                                            <Td>
                                                <HStack spacing={1}>
                                                    <Badge colorScheme={dep.color} fontSize="10px" borderRadius="md" px={2}>{dep.label}</Badge>
                                                    {a.modo === "crm" && <Badge colorScheme="purple" fontSize="9px" borderRadius="md" px={1}>Gerente CC</Badge>}
                                                </HStack>
                                            </Td>
                                            <Td isNumeric>
                                                {m.nota_geral != null ? (
                                                    <Badge colorScheme={notaColor(m.nota_geral)} fontSize="11px" borderRadius="md" px={2}>
                                                        {Number(m.nota_geral).toFixed(1)}
                                                    </Badge>
                                                ) : <Text fontSize="xs" color="gray.400">—</Text>}
                                            </Td>
                                            {a.modo === "crm" ? (
                                                <CrmRankingCells tarefas={item.tarefas} />
                                            ) : (
                                                <>
                                                    <Td isNumeric>
                                                        {tempoResp != null ? (
                                                            <Text fontSize="xs" color={tempoAlto ? "red.500" : "gray.700"} fontWeight={tempoAlto ? "bold" : "normal"}>
                                                                {formatSeg(tempoResp)}
                                                            </Text>
                                                        ) : <Text fontSize="xs" color="gray.400">—</Text>}
                                                    </Td>
                                                    <Td isNumeric>
                                                        {pctSLA != null ? (
                                                            <Text fontSize="xs" color={pctSLA >= 85 ? "green.600" : pctSLA >= 60 ? "yellow.600" : "red.500"} fontWeight="semibold">
                                                                {Number(pctSLA).toFixed(1)}%
                                                            </Text>
                                                        ) : <Text fontSize="xs" color="gray.400">—</Text>}
                                                    </Td>
                                                    <Td isNumeric><Text fontSize="xs" color="gray.700">{m.contatos_total ?? 0}</Text></Td>
                                                    <Td isNumeric><Text fontSize="xs" color="gray.700">{m.contatos_fechados ?? 0}</Text></Td>
                                                    <Td isNumeric>
                                                        {a.departamento === "comercial" ? (
                                                            <Text fontSize="xs" color="gray.700">{m.perguntas_ml_respondidas ?? 0}</Text>
                                                        ) : <Text fontSize="xs" color="gray.400">—</Text>}
                                                    </Td>
                                                </>
                                            )}
                                            <Td textAlign="center">
                                                <IconButton as={RouterLink} to={`/desempenho/atendentes/${a.id}`} icon={<Eye size={14} />} size="xs" variant="ghost" colorScheme="blue" aria-label="Histórico" />
                                            </Td>
                                        </Tr>
                                    );
                                })}
                            </Tbody>
                        </Table>
                    </Box>
                )}
            </Box>

            {/* Mini gráfico de notas */}
            {!loading && atendentes.length > 0 && (
                <Box mt={5} bg="white" borderRadius="xl" boxShadow="sm" border="1px solid" borderColor="gray.200" p={4}>
                    <Text fontSize="sm" fontWeight="bold" color="gray.700" mb={3}>Notas por Atendente</Text>
                    <Box h="220px">
                        <ResponsiveContainer width="100%" height="100%">
                            <BarChart data={atendentes.map(it => ({ name: (it.atendente?.apelido_poli || it.atendente?.user?.name || "").split(" ")[0], nota: Number(it.metrica?.nota_geral || 0) }))}>
                                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f1f5f9" />
                                <XAxis dataKey="name" fontSize={11} stroke="#94a3b8" />
                                <YAxis domain={[0, 100]} fontSize={11} stroke="#94a3b8" />
                                <RechartsTooltip />
                                <Bar dataKey="nota" radius={[6, 6, 0, 0]}>
                                    {atendentes.map((it, i) => {
                                        const c = notaColor(it.metrica?.nota_geral);
                                        const map = { green: "#10b981", blue: "#3b82f6", yellow: "#f59e0b", red: "#ef4444" };
                                        return <Cell key={i} fill={map[c] || "#94a3b8"} />;
                                    })}
                                </Bar>
                            </BarChart>
                        </ResponsiveContainer>
                    </Box>
                </Box>
            )}
        </Box>
    );
};

const MetricCard = ({ label, value, icon: Icon, color, loading, isText }) => (
    <Box bg="white" borderRadius="xl" boxShadow="sm" border="1px solid" borderColor="gray.200" p={4}>
        <Flex align="center" gap={3}>
            <Flex align="center" justify="center" w="40px" h="40px" borderRadius="lg" bg={`${color}.50`}>
                <Icon size={20} color={`var(--chakra-colors-${color}-500)`} />
            </Flex>
            <Box flex={1} minW={0}>
                <Text fontSize="10px" color="gray.500" fontWeight="semibold" textTransform="uppercase">{label}</Text>
                {loading ? <Skeleton height="24px" w="80%" mt={1} /> : (
                    <Text fontSize={isText ? "md" : "xl"} fontWeight="bold" color="gray.800">{value}</Text>
                )}
            </Box>
        </Flex>
    </Box>
);

// ===== ATENDENTES TAB =====
const AtendentesTab = () => {
    const toast = useToast();
    const [loading, setLoading] = useState(true);
    const [atendentes, setAtendentes] = useState([]);
    const [filtroDep, setFiltroDep] = useState("");
    const [filtroAtivo, setFiltroAtivo] = useState("1");

    const { isOpen, onOpen, onClose } = useDisclosure();
    const [editing, setEditing] = useState(null);
    const [users, setUsers] = useState([]);
    const [poliUsers, setPoliUsers] = useState([]);
    const [loadingPoli, setLoadingPoli] = useState(false);

    const [form, setForm] = useState({
        user_id: "", poli_user_id: "", departamento: "comercial", modo: "poli",
        departamento_id_poli: 158624, apelido_poli: "", data_inicio: "",
    });

    const { isOpen: isDelOpen, onOpen: onDelOpen, onClose: onDelClose } = useDisclosure();
    const [delTarget, setDelTarget] = useState(null);
    const cancelRef = useRef();

    const fetchAtendentes = useCallback(async () => {
        setLoading(true);
        const params = {};
        if (filtroDep) params.departamento = filtroDep;
        if (filtroAtivo !== "") params.ativo = filtroAtivo;
        const res = await getDesempenhoAtendentes(params);
        if (res && res.data) setAtendentes(res.data);
        else setAtendentes([]);
        setLoading(false);
    }, [filtroDep, filtroAtivo]);

    useEffect(() => { fetchAtendentes(); }, [fetchAtendentes]);

    useEffect(() => {
        (async () => {
            const res = await getUsuarios({ per_page: 500 });
            if (res && res.data) setUsers(res.data);
        })();
    }, []);

    const loadPoliUsers = async (depId) => {
        setLoadingPoli(true);
        const res = await getDesempenhoPoliUsers({ departamento_id: depId });
        if (res && res.data) setPoliUsers(res.data);
        else setPoliUsers([]);
        setLoadingPoli(false);
    };

    const openCreate = () => {
        setEditing(null);
        setForm({
            user_id: "", poli_user_id: "", departamento: "comercial", modo: "poli", departamento_id_poli: 158624,
            apelido_poli: "", data_inicio: "",
        });
        loadPoliUsers(158624);
        onOpen();
    };

    const openEdit = (a) => {
        setEditing(a);
        setForm({
            user_id: a.user_id || "", poli_user_id: a.poli_user_id || "",
            departamento: a.departamento, modo: a.modo || "poli", departamento_id_poli: a.departamento_id_poli || 158624,
            apelido_poli: a.apelido_poli || "",
            data_inicio: a.data_inicio ? String(a.data_inicio).slice(0, 10) : "",
            data_fim: a.data_fim ? String(a.data_fim).slice(0, 10) : "",
            ativo: a.ativo,
        });
        loadPoliUsers(a.departamento_id_poli || 158624);
        onOpen();
    };

    const handleDepChange = (dep) => {
        const ids = { comercial: 158624, suporte: 158625, financeiro: 158626 };
        const newId = ids[dep];
        setForm(f => ({ ...f, departamento: dep, departamento_id_poli: newId }));
        loadPoliUsers(newId);
    };

    const handleSave = async () => {
        const ehCrm = form.modo === "crm";
        if (!form.user_id || (!ehCrm && (!form.poli_user_id || !form.apelido_poli))) {
            toast({ status: "warning", title: ehCrm ? "Selecione o usuário do sistema." : "Preencha usuário, poli_user e apelido." });
            return;
        }
        const payload = { ...form };
        payload.user_id = Number(payload.user_id);
        payload.poli_user_id = Number(payload.poli_user_id || 0);
        payload.departamento_id_poli = Number(payload.departamento_id_poli);
        if (!payload.data_inicio) delete payload.data_inicio;
        if (!payload.data_fim) delete payload.data_fim;

        const res = editing
            ? await updateDesempenhoAtendente(editing.id, payload)
            : await createDesempenhoAtendente(payload);
        if (res && (res.data || res.id)) {
            toast({ status: "success", title: editing ? "Atendente atualizado" : "Atendente criado" });
            onClose();
            fetchAtendentes();
        } else {
            const msg = res?.data?.message || res?.message || "Erro ao salvar";
            toast({ status: "error", title: msg });
        }
    };

    const confirmDelete = async () => {
        if (!delTarget) return;
        const res = await deleteDesempenhoAtendente(delTarget.id);
        if (res) {
            toast({ status: "success", title: "Atendente desativado" });
            onDelClose();
            fetchAtendentes();
        } else {
            toast({ status: "error", title: "Erro ao desativar" });
        }
    };

    return (
        <Box>
            <Flex justify="space-between" align="center" mb={4} flexWrap="wrap" gap={3}>
                <HStack spacing={3} flexWrap="wrap">
                    <Select size="sm" bg="white" borderRadius="md" value={filtroDep} onChange={(e) => setFiltroDep(e.target.value)} w="170px">
                        <option value="">Todos departamentos</option>
                        <option value="comercial">Comercial</option>
                        <option value="suporte">Suporte</option>
                        <option value="financeiro">Financeiro</option>
                    </Select>
                    <Select size="sm" bg="white" borderRadius="md" value={filtroAtivo} onChange={(e) => setFiltroAtivo(e.target.value)} w="150px">
                        <option value="1">Apenas Ativos</option>
                        <option value="0">Apenas Inativos</option>
                        <option value="">Todos</option>
                    </Select>
                </HStack>
                <Button size="sm" leftIcon={<Plus size={15} />} colorScheme="orange" onClick={openCreate}>Vincular Atendente</Button>
            </Flex>

            <Box bg="white" borderRadius="xl" boxShadow="sm" border="1px solid" borderColor="gray.200" overflow="hidden">
                {loading ? (
                    <Flex justify="center" py={10}><Spinner color="orange.500" /></Flex>
                ) : atendentes.length === 0 ? (
                    <Text textAlign="center" py={10} color="gray.400" fontSize="sm">Nenhum atendente encontrado</Text>
                ) : (
                    <Box overflowX="auto">
                        <Table size="sm" variant="simple">
                            <Thead bg="gray.50">
                                <Tr>
                                    <Th fontSize="xs" color="gray.500">ID</Th>
                                    <Th fontSize="xs" color="gray.500">Nome</Th>
                                    <Th fontSize="xs" color="gray.500">Apelido POLI</Th>
                                    <Th fontSize="xs" color="gray.500">POLI User</Th>
                                    <Th fontSize="xs" color="gray.500">Depto</Th>
                                    <Th fontSize="xs" color="gray.500">Início</Th>
                                    <Th fontSize="xs" color="gray.500">Status</Th>
                                    <Th fontSize="xs" color="gray.500" textAlign="center">Ações</Th>
                                </Tr>
                            </Thead>
                            <Tbody>
                                {atendentes.map((a) => {
                                    const dep = DEP_BADGE[a.departamento] || { label: a.departamento, color: "gray" };
                                    return (
                                        <Tr key={a.id} _hover={{ bg: "gray.50" }}>
                                            <Td fontSize="xs" color="gray.600">{a.id}</Td>
                                            <Td>
                                                <HStack>
                                                    <Avatar size="xs" name={a.user?.name || a.apelido_poli} />
                                                    <Box minW={0}>
                                                        <Text fontSize="sm" fontWeight="semibold" color="gray.800" noOfLines={1}>{a.user?.name || "—"}</Text>
                                                        <Text fontSize="10px" color="gray.500" noOfLines={1}>{a.user?.email || "—"}</Text>
                                                    </Box>
                                                </HStack>
                                            </Td>
                                            <Td fontSize="xs" color="gray.700">{a.apelido_poli}</Td>
                                            <Td fontSize="xs" color="gray.600">{a.poli_user_id}</Td>
                                            <Td><Badge colorScheme={dep.color} fontSize="10px" borderRadius="md" px={2}>{dep.label}</Badge></Td>
                                            <Td fontSize="xs" color="gray.600">{formatDate(a.data_inicio)}</Td>
                                            <Td>
                                                <Badge colorScheme={a.ativo ? "green" : "gray"} fontSize="10px">{a.ativo ? "Ativo" : "Inativo"}</Badge>
                                            </Td>
                                            <Td textAlign="center">
                                                <HStack spacing={1} justify="center">
                                                    <IconButton as={RouterLink} to={`/desempenho/atendentes/${a.id}`} icon={<Eye size={14} />} size="xs" variant="ghost" colorScheme="blue" aria-label="Histórico" />
                                                    <IconButton icon={<Pencil size={14} />} size="xs" variant="ghost" colorScheme="orange" aria-label="Editar" onClick={() => openEdit(a)} />
                                                    <IconButton icon={<Trash2 size={14} />} size="xs" variant="ghost" colorScheme="red" aria-label="Desativar" onClick={() => { setDelTarget(a); onDelOpen(); }} isDisabled={!a.ativo} />
                                                </HStack>
                                            </Td>
                                        </Tr>
                                    );
                                })}
                            </Tbody>
                        </Table>
                    </Box>
                )}
            </Box>

            {/* Modal Criar/Editar */}
            <Modal isOpen={isOpen} onClose={onClose} size="xl" isCentered>
                <ModalOverlay />
                <ModalContent borderRadius="xl">
                    <ModalHeader fontSize="md" pb={2}>{editing ? "Editar Atendente" : "Vincular Atendente"}</ModalHeader>
                    <ModalCloseButton />
                    <ModalBody pb={4}>
                        <SimpleGrid columns={[1, 2]} spacing={4}>
                            <FormControl isRequired>
                                <FormLabel fontSize="xs" fontWeight="semibold" color="gray.600">Usuário do sistema</FormLabel>
                                <Select size="sm" borderRadius="md" value={form.user_id} onChange={(e) => setForm(f => ({ ...f, user_id: e.target.value }))} placeholder="Selecione">
                                    {semBloqueados(users, form.user_id).map(u => <option key={u.id} value={u.id}>{u.name} — {u.email}</option>)}
                                </Select>
                            </FormControl>
                            <FormControl isRequired>
                                <FormLabel fontSize="xs" fontWeight="semibold" color="gray.600">Departamento</FormLabel>
                                <Select size="sm" borderRadius="md" value={form.departamento} onChange={(e) => handleDepChange(e.target.value)}>
                                    <option value="comercial">Comercial</option>
                                    <option value="suporte">Suporte</option>
                                    <option value="financeiro">Financeiro</option>
                                </Select>
                            </FormControl>
                            <FormControl isRequired gridColumn={["auto", "span 2"]}>
                                <FormLabel fontSize="xs" fontWeight="semibold" color="gray.600">Modo de atendimento</FormLabel>
                                <Select size="sm" borderRadius="md" value={form.modo} onChange={(e) => setForm(f => ({ ...f, modo: e.target.value }))}>
                                    <option value="poli">Atendimento na POLI (avaliação por SLA / IA)</option>
                                    <option value="crm">Gerente de contas (avaliação por métricas do CRM)</option>
                                </Select>
                                <Text fontSize="10px" color="gray.400" mt={1}>
                                    {form.modo === "crm"
                                        ? "Pontuado por ligações, mensagens, clientes contatados e tarefas do CRM. Não consome a POLI."
                                        : "Pontuado pelas conversas da POLI (SLA, avaliação de IA, perguntas ML)."}
                                </Text>
                            </FormControl>
                            {form.modo !== "crm" && (<>
                            <FormControl isRequired>
                                <FormLabel fontSize="xs" fontWeight="semibold" color="gray.600">
                                    Usuário POLI {loadingPoli && <Spinner size="xs" ml={2} />}
                                </FormLabel>
                                <Select size="sm" borderRadius="md" value={form.poli_user_id} onChange={(e) => {
                                    const pu = poliUsers.find(p => String(p.poli_user_id) === e.target.value);
                                    setForm(f => ({
                                        ...f, poli_user_id: e.target.value,
                                        apelido_poli: f.apelido_poli || pu?.nome || "",
                                    }));
                                }} placeholder="Selecione">
                                    {poliUsers.map(p => (
                                        <option key={p.poli_user_id} value={p.poli_user_id}>
                                            {p.nome} — {p.poli_user_id}
                                        </option>
                                    ))}
                                </Select>
                                {!loadingPoli && poliUsers.length === 0 && (
                                    <Text fontSize="10px" color="yellow.600" mt={1}>Nenhum usuário encontrado. Insira o ID manualmente abaixo.</Text>
                                )}
                            </FormControl>
                            <FormControl>
                                <FormLabel fontSize="xs" fontWeight="semibold" color="gray.600">Poli User ID (manual)</FormLabel>
                                <Input size="sm" borderRadius="md" type="number" value={form.poli_user_id} onChange={(e) => setForm(f => ({ ...f, poli_user_id: e.target.value }))} placeholder="Ex: 100106" />
                            </FormControl>
                            <FormControl isRequired>
                                <FormLabel fontSize="xs" fontWeight="semibold" color="gray.600">Apelido POLI</FormLabel>
                                <Input size="sm" borderRadius="md" value={form.apelido_poli} onChange={(e) => setForm(f => ({ ...f, apelido_poli: e.target.value }))} placeholder="Nome como aparece na POLI" />
                            </FormControl>
                            </>)}
                            <FormControl>
                                <FormLabel fontSize="xs" fontWeight="semibold" color="gray.600">Data de Início</FormLabel>
                                <Input size="sm" borderRadius="md" type="date" value={form.data_inicio} onChange={(e) => setForm(f => ({ ...f, data_inicio: e.target.value }))} />
                            </FormControl>
                            {editing && (
                                <>
                                    <FormControl>
                                        <FormLabel fontSize="xs" fontWeight="semibold" color="gray.600">Data de Desligamento</FormLabel>
                                        <Input size="sm" borderRadius="md" type="date" value={form.data_fim || ""} onChange={(e) => setForm(f => ({ ...f, data_fim: e.target.value }))} />
                                    </FormControl>
                                    <FormControl display="flex" alignItems="center" mt={5}>
                                        <FormLabel fontSize="xs" fontWeight="semibold" color="gray.600" mb={0}>Ativo</FormLabel>
                                        <Switch colorScheme="orange" isChecked={form.ativo} onChange={(e) => setForm(f => ({ ...f, ativo: e.target.checked }))} />
                                    </FormControl>
                                </>
                            )}
                        </SimpleGrid>
                    </ModalBody>
                    <ModalFooter pt={0}>
                        <Button size="sm" variant="ghost" mr={2} onClick={onClose}>Cancelar</Button>
                        <Button size="sm" colorScheme="orange" onClick={handleSave}>{editing ? "Salvar" : "Criar"}</Button>
                    </ModalFooter>
                </ModalContent>
            </Modal>

            {/* Confirm delete */}
            <AlertDialog isOpen={isDelOpen} leastDestructiveRef={cancelRef} onClose={onDelClose} isCentered>
                <AlertDialogOverlay>
                    <AlertDialogContent>
                        <AlertDialogHeader fontSize="md">Desativar atendente</AlertDialogHeader>
                        <AlertDialogBody fontSize="sm">
                            Tem certeza? O atendente <Text as="span" fontWeight="bold">{delTarget?.apelido_poli}</Text> ficará inativo e deixará de ser avaliado.
                        </AlertDialogBody>
                        <AlertDialogFooter>
                            <Button size="sm" ref={cancelRef} onClick={onDelClose}>Cancelar</Button>
                            <Button size="sm" colorScheme="red" onClick={confirmDelete} ml={3}>Desativar</Button>
                        </AlertDialogFooter>
                    </AlertDialogContent>
                </AlertDialogOverlay>
            </AlertDialog>
        </Box>
    );
};

// ===== AVALIACOES TAB =====
const AvaliacoesTab = ({ initialAtendenteId = "" }) => {
    const toast = useToast();
    const [loading, setLoading] = useState(true);
    const [avaliacoes, setAvaliacoes] = useState([]);
    const [meta, setMeta] = useState(null);
    const [page, setPage] = useState(1);

    const initialRange = rangeFromPreset("30d");
    const [preset, setPreset] = useState("30d");
    const [inicio, setInicio] = useState(initialRange.inicio);
    const [fim, setFim] = useState(initialRange.fim);

    const [filtros, setFiltros] = useState({
        atendente_id: initialAtendenteId || "", status: "", categoria_resolucao: "",
    });

    useEffect(() => {
        if (initialAtendenteId) {
            setFiltros(f => f.atendente_id === initialAtendenteId ? f : { ...f, atendente_id: initialAtendenteId });
            setPage(1);
        }
    }, [initialAtendenteId]);

    const [atendentes, setAtendentes] = useState([]);

    const [selectedId, setSelectedId] = useState(null);
    const [selectedDetail, setSelectedDetail] = useState(null);
    const [loadingDetail, setLoadingDetail] = useState(false);
    const { isOpen: drawerOpen, onOpen: openDrawer, onClose: closeDrawer } = useDisclosure();

    const handlePresetChange = (p) => {
        setPreset(p);
        setPage(1);
        if (p !== "custom") {
            const r = rangeFromPreset(p);
            if (r) { setInicio(r.inicio); setFim(r.fim); }
        }
    };

    useEffect(() => {
        (async () => {
            const res = await getDesempenhoAtendentes({ ativo: 1 });
            if (res && res.data) setAtendentes(res.data.filter(a => !a.user?.bloqueado));
        })();
    }, []);

    const fetchAvaliacoes = useCallback(async () => {
        if (!inicio || !fim) return;
        setLoading(true);
        const params = { page, per_page: 20, inicio, fim };
        Object.entries(filtros).forEach(([k, v]) => { if (v) params[k] = v; });
        const res = await getDesempenhoAvaliacoes(params);
        if (res) {
            setAvaliacoes(res.data || []);
            setMeta({
                current_page: res.current_page || 1,
                last_page: res.last_page || 1,
                total: res.total || 0,
            });
        }
        setLoading(false);
    }, [filtros, page, inicio, fim]);

    useEffect(() => { fetchAvaliacoes(); }, [fetchAvaliacoes]);

    const openDetail = async (id) => {
        setSelectedId(id);
        openDrawer();
        setLoadingDetail(true);
        setSelectedDetail(null);
        const res = await getDesempenhoAvaliacao(id);
        if (res) setSelectedDetail(res.data || res);
        else toast({ status: "error", title: "Erro ao carregar avaliação" });
        setLoadingDetail(false);
    };

    const reavaliar = async (item) => {
        if (!item) return;
        toast({ status: "info", title: "Reavaliação disparada. Pode levar alguns minutos..." });
        const res = await rodarDesempenhoAvaliacoes({
            data: item.data_referencia,
            atendente_id: item.atendente_id,
            contact_id: item.poli_contact_id,
        });
        if (res) {
            toast({ status: "success", title: "Reavaliação concluída" });
            fetchAvaliacoes();
            if (selectedId === item.id) openDetail(item.id);
        } else {
            toast({ status: "error", title: "Erro ao reavaliar" });
        }
    };

    return (
        <Box>
            <Flex align="flex-end" mb={4} flexWrap="wrap" gap={3}>
                <PeriodoFiltro
                    preset={preset}
                    inicio={inicio}
                    fim={fim}
                    onPresetChange={handlePresetChange}
                    onInicioChange={(v) => { setPage(1); setInicio(v); }}
                    onFimChange={(v) => { setPage(1); setFim(v); }}
                />
                <Select size="sm" bg="white" borderRadius="md" w="220px" value={filtros.atendente_id} onChange={(e) => { setPage(1); setFiltros(f => ({ ...f, atendente_id: e.target.value })); }}>
                    <option value="">Todos atendentes</option>
                    {atendentes.map(a => <option key={a.id} value={a.id}>{a.user?.name || a.apelido_poli}</option>)}
                </Select>
                <Select size="sm" bg="white" borderRadius="md" w="160px" value={filtros.status} onChange={(e) => { setPage(1); setFiltros(f => ({ ...f, status: e.target.value })); }}>
                    <option value="">Todos status</option>
                    {Object.entries(STATUS_AVAL).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
                </Select>
                <Select size="sm" bg="white" borderRadius="md" w="170px" value={filtros.categoria_resolucao} onChange={(e) => { setPage(1); setFiltros(f => ({ ...f, categoria_resolucao: e.target.value })); }}>
                    <option value="">Todas categorias</option>
                    {Object.entries(CAT_RESOLUCAO).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
                </Select>
                <Box ml="auto">
                    <Text fontSize="xs" color="gray.500">{meta ? `${meta.total} avaliação(ões)` : ""}</Text>
                </Box>
            </Flex>

            <Box bg="white" borderRadius="xl" boxShadow="sm" border="1px solid" borderColor="gray.200" overflow="hidden">
                {loading ? (
                    <Flex justify="center" py={10}><Spinner color="orange.500" /></Flex>
                ) : avaliacoes.length === 0 ? (
                    <Text textAlign="center" py={10} color="gray.400" fontSize="sm">Nenhuma avaliação encontrada</Text>
                ) : (
                    <Box overflowX="auto">
                        <Table size="sm" variant="simple">
                            <Thead bg="gray.50">
                                <Tr>
                                    <Th fontSize="xs">Data</Th>
                                    <Th fontSize="xs">Atendente</Th>
                                    <Th fontSize="xs">Cliente</Th>
                                    <Th fontSize="xs">Categoria</Th>
                                    <Th fontSize="xs" isNumeric>Nota</Th>
                                    <Th fontSize="xs">Status</Th>
                                    <Th fontSize="xs" textAlign="center">Ações</Th>
                                </Tr>
                            </Thead>
                            <Tbody>
                                {avaliacoes.map((av) => {
                                    const cat = CAT_RESOLUCAO[av.categoria_resolucao] || { label: av.categoria_resolucao || "—", color: "gray" };
                                    const st = STATUS_AVAL[av.status] || { label: av.status, color: "gray" };
                                    return (
                                        <Tr key={av.id} _hover={{ bg: "gray.50" }} cursor="pointer" onClick={() => openDetail(av.id)}>
                                            <Td fontSize="xs" color="gray.600">{formatDate(av.data_referencia)}</Td>
                                            <Td>
                                                <Text fontSize="sm" fontWeight="semibold" color="gray.800">{av.atendente?.apelido_poli || av.atendente?.user?.name || "—"}</Text>
                                            </Td>
                                            <Td fontSize="sm" color="gray.700" maxW="220px"><Text noOfLines={1}>{av.nome_cliente || "—"}</Text></Td>
                                            <Td><Badge colorScheme={cat.color} fontSize="10px" borderRadius="md">{cat.label}</Badge></Td>
                                            <Td isNumeric>
                                                {av.nota != null ? (
                                                    <Badge colorScheme={notaColor(av.nota)} fontSize="11px" borderRadius="md" px={2}>
                                                        {Number(av.nota).toFixed(1)}
                                                    </Badge>
                                                ) : <Text fontSize="xs" color="gray.400">—</Text>}
                                            </Td>
                                            <Td><Badge colorScheme={st.color} fontSize="10px">{st.label}</Badge></Td>
                                            <Td textAlign="center" onClick={(e) => e.stopPropagation()}>
                                                <Menu>
                                                    <MenuButton as={IconButton} icon={<ChevronRight size={14} />} size="xs" variant="ghost" aria-label="menu" />
                                                    <MenuList>
                                                        <MenuItem icon={<Eye size={14} />} onClick={() => openDetail(av.id)} fontSize="sm">Ver detalhe</MenuItem>
                                                        <MenuItem icon={<Zap size={14} />} onClick={() => reavaliar(av)} fontSize="sm">Reavaliar</MenuItem>
                                                    </MenuList>
                                                </Menu>
                                            </Td>
                                        </Tr>
                                    );
                                })}
                            </Tbody>
                        </Table>
                    </Box>
                )}
                {meta && meta.last_page > 1 && (
                    <Flex justify="space-between" align="center" px={4} py={3} borderTop="1px solid" borderColor="gray.100">
                        <Text fontSize="xs" color="gray.500">Página {meta.current_page} de {meta.last_page}</Text>
                        <HStack spacing={2}>
                            <IconButton icon={<ChevronLeft size={14} />} size="xs" variant="outline" isDisabled={page <= 1} onClick={() => setPage(p => p - 1)} aria-label="Ant" />
                            <IconButton icon={<ChevronRight size={14} />} size="xs" variant="outline" isDisabled={page >= meta.last_page} onClick={() => setPage(p => p + 1)} aria-label="Prox" />
                        </HStack>
                    </Flex>
                )}
            </Box>

            <AvaliacaoDrawer isOpen={drawerOpen} onClose={closeDrawer} loading={loadingDetail} detail={selectedDetail} onReavaliar={reavaliar} />
        </Box>
    );
};

const filtrarInstrucaoInterna = (historico) => {
    if (!Array.isArray(historico) || historico.length === 0) return [];
    const last = historico[historico.length - 1];
    if (!last || last.role !== "user") return historico;
    const textos = [];
    if (typeof last.content === "string") textos.push(last.content);
    else if (Array.isArray(last.content)) {
        last.content.forEach(b => { if (b?.type === "text" && typeof b.text === "string") textos.push(b.text); });
    }
    if (textos.some(t => t.includes("[INSTRUÇÃO INTERNA"))) return historico.slice(0, -1);
    return historico;
};

const AvaliacaoDrawer = ({ isOpen, onClose, loading, detail, onReavaliar }) => {
    const cat = detail && (CAT_RESOLUCAO[detail.categoria_resolucao] || { label: detail.categoria_resolucao, color: "gray" });
    const historico = filtrarInstrucaoInterna(detail?.historico_json);

    return (
        <Drawer isOpen={isOpen} placement="right" onClose={onClose} size="lg">
            <DrawerOverlay />
            <DrawerContent>
                <DrawerHeader borderBottomWidth="1px" px={5} py={4}>
                    <Flex justify="space-between" align="center">
                        <HStack>
                            <Sparkles size={18} color="#9333ea" />
                            <Text fontSize="md" fontWeight="bold">Avaliação IA</Text>
                        </HStack>
                        <DrawerCloseButton position="static" />
                    </Flex>
                </DrawerHeader>
                <DrawerBody px={5} py={4}>
                    {loading ? (
                        <VStack spacing={3} align="stretch">
                            <Skeleton height="80px" />
                            <Skeleton height="60px" />
                            <Skeleton height="200px" />
                        </VStack>
                    ) : !detail ? (
                        <Text color="gray.400" fontSize="sm">Nada para mostrar</Text>
                    ) : (
                        <VStack spacing={4} align="stretch">
                            {/* Header info */}
                            <Box bg="gray.50" borderRadius="lg" p={3}>
                                <Flex justify="space-between" align="start" gap={3} flexWrap="wrap">
                                    <Box minW={0}>
                                        <Text fontSize="xs" color="gray.500" textTransform="uppercase" fontWeight="semibold">Cliente</Text>
                                        <Text fontSize="sm" fontWeight="semibold" color="gray.800">{detail.nome_cliente || "—"}</Text>
                                        <Text fontSize="10px" color="gray.500" mt={0.5}>Atendimento em {formatDate(detail.data_referencia)}</Text>
                                    </Box>
                                    <Box textAlign="right">
                                        {detail.nota != null && (
                                            <Badge colorScheme={notaColor(detail.nota)} fontSize="xl" borderRadius="lg" px={3} py={1}>
                                                {Number(detail.nota).toFixed(1)}
                                            </Badge>
                                        )}
                                        {cat && <Box mt={2}><Badge colorScheme={cat.color} fontSize="10px" borderRadius="md">{cat.label}</Badge></Box>}
                                    </Box>
                                </Flex>
                                {detail.atendente && (
                                    <HStack mt={3} spacing={2}>
                                        <Avatar size="xs" name={detail.atendente.apelido_poli || detail.atendente.user?.name} />
                                        <Box>
                                            <Text fontSize="xs" fontWeight="semibold" color="gray.700">{detail.atendente.user?.name || detail.atendente.apelido_poli}</Text>
                                            <Text fontSize="10px" color="gray.500">{DEP_BADGE[detail.atendente.departamento]?.label}</Text>
                                        </Box>
                                    </HStack>
                                )}
                            </Box>

                            {/* Feedback Claude */}
                            {detail.feedback && (
                                <Box>
                                    <Text fontSize="xs" color="gray.500" textTransform="uppercase" fontWeight="semibold" mb={1}>Feedback do Claude</Text>
                                    <Box bg="purple.50" borderLeft="3px solid" borderColor="purple.400" p={3} borderRadius="md">
                                        <Text fontSize="sm" color="gray.800" whiteSpace="pre-wrap">{detail.feedback}</Text>
                                    </Box>
                                </Box>
                            )}

                            {/* Pontos fortes / melhorar */}
                            <SimpleGrid columns={[1, 2]} spacing={3}>
                                {Array.isArray(detail.pontos_fortes) && detail.pontos_fortes.length > 0 && (
                                    <Box>
                                        <Text fontSize="xs" color="green.600" textTransform="uppercase" fontWeight="semibold" mb={1.5}>Pontos Fortes</Text>
                                        <VStack align="stretch" spacing={1.5}>
                                            {detail.pontos_fortes.map((p, i) => (
                                                <HStack key={i} align="start">
                                                    <Box mt={0.5}><CheckCircle2 size={14} color="#16a34a" /></Box>
                                                    <Text fontSize="xs" color="gray.700">{p}</Text>
                                                </HStack>
                                            ))}
                                        </VStack>
                                    </Box>
                                )}
                                {Array.isArray(detail.pontos_melhorar) && detail.pontos_melhorar.length > 0 && (
                                    <Box>
                                        <Text fontSize="xs" color="orange.600" textTransform="uppercase" fontWeight="semibold" mb={1.5}>Pontos a Melhorar</Text>
                                        <VStack align="stretch" spacing={1.5}>
                                            {detail.pontos_melhorar.map((p, i) => (
                                                <HStack key={i} align="start">
                                                    <Box mt={0.5}><AlertCircle size={14} color="#ea580c" /></Box>
                                                    <Text fontSize="xs" color="gray.700">{p}</Text>
                                                </HStack>
                                            ))}
                                        </VStack>
                                    </Box>
                                )}
                            </SimpleGrid>

                            {/* Meta info */}
                            <Flex justify="space-between" align="center" fontSize="10px" color="gray.500" flexWrap="wrap" gap={2}>
                                <HStack><FileText size={11} /> <Text>{detail.modelo_ia || "claude-opus-4-7"}</Text></HStack>
                                {detail.tokens_input != null && <Text>in: {detail.tokens_input} / out: {detail.tokens_output || 0} tokens</Text>}
                                {detail.avaliado_em && <Text>Avaliado em {new Date(detail.avaliado_em).toLocaleString("pt-BR")}</Text>}
                            </Flex>

                            {detail.erro && (
                                <Box bg="red.50" border="1px solid" borderColor="red.200" borderRadius="md" p={3}>
                                    <HStack spacing={2} mb={1}><AlertCircle size={14} color="#dc2626" /><Text fontSize="xs" fontWeight="bold" color="red.700">Erro na avaliação</Text></HStack>
                                    <Text fontSize="xs" color="red.700" whiteSpace="pre-wrap">{detail.erro}</Text>
                                </Box>
                            )}

                            <Divider />

                            {/* Histórico Chat */}
                            <Box>
                                <Text fontSize="xs" color="gray.500" textTransform="uppercase" fontWeight="semibold" mb={2}>Histórico da Conversa</Text>
                                {historico.length === 0 ? (
                                    <Text fontSize="xs" color="gray.400" fontStyle="italic">Histórico não disponível</Text>
                                ) : (
                                    <VStack spacing={2} align="stretch">
                                        {historico.map((msg, i) => <ChatMessage key={i} msg={msg} />)}
                                    </VStack>
                                )}
                            </Box>

                            <Divider />
                            <HStack justify="flex-end">
                                <Button size="sm" leftIcon={<Zap size={14} />} colorScheme="purple" variant="outline" onClick={() => onReavaliar(detail)}>Reavaliar</Button>
                            </HStack>
                        </VStack>
                    )}
                </DrawerBody>
            </DrawerContent>
        </Drawer>
    );
};

const ChatMessage = ({ msg }) => {
    const role = msg.role;
    const blocks = Array.isArray(msg.content) ? msg.content : [{ type: "text", text: String(msg.content || "") }];
    const isUser = role === "user";

    return (
        <Flex justify={isUser ? "flex-start" : "flex-end"}>
            <Box
                maxW="85%"
                bg={isUser ? "gray.100" : "orange.50"}
                borderLeft={isUser ? "3px solid" : "none"}
                borderRight={!isUser ? "3px solid" : "none"}
                borderColor={isUser ? "gray.400" : "orange.400"}
                borderRadius="md"
                px={3} py={2}
            >
                <Text fontSize="10px" fontWeight="bold" color={isUser ? "gray.600" : "orange.600"} textTransform="uppercase" mb={1}>
                    {isUser ? "Cliente" : "Atendente"}
                </Text>
                {blocks.map((b, i) => {
                    if (b.type === "text") {
                        return <Text key={i} fontSize="xs" color="gray.800" whiteSpace="pre-wrap">{b.text}</Text>;
                    }
                    if (b.type === "image") {
                        const url = b.source?.url || b.source?.data;
                        return (
                            <Box key={i} mt={1}>
                                {url ? (
                                    <img src={url} alt="" style={{ maxWidth: "100%", borderRadius: 6, maxHeight: 280 }} />
                                ) : (
                                    <HStack color="gray.500" fontSize="xs"><ImageIcon size={12} /><Text>(imagem)</Text></HStack>
                                )}
                            </Box>
                        );
                    }
                    if (b.type === "document") {
                        return (
                            <HStack key={i} color="gray.500" fontSize="xs" mt={1}><FileText size={12} /><Text>(documento)</Text></HStack>
                        );
                    }
                    return <Text key={i} fontSize="10px" color="gray.400" fontStyle="italic">({b.type})</Text>;
                })}
            </Box>
        </Flex>
    );
};

// ===== CONFIGURACOES TAB =====
const ConfiguracoesTab = () => {
    const toast = useToast();
    const [loadingDeps, setLoadingDeps] = useState(true);
    const [deps, setDeps] = useState([]);
    const [loadingTmpl, setLoadingTmpl] = useState(true);
    const [templates, setTemplates] = useState([]);

    // Templates form
    const { isOpen: tmplOpen, onOpen: tmplOnOpen, onClose: tmplOnClose } = useDisclosure();
    const [editingTmpl, setEditingTmpl] = useState(null);
    const [tmplForm, setTmplForm] = useState({
        codigo: "", nome: "", tipo: "custom", descricao: "", peso: 10,
        departamento_filtro: "todos", modo: "poli", meta_diaria: "", config_json: "{}", calculador: "",
    });

    const fetchDeps = async () => {
        setLoadingDeps(true);
        const res = await getDesempenhoDepartamentosConfigs();
        if (res) setDeps(res.data || res);
        setLoadingDeps(false);
    };
    const fetchTemplates = async () => {
        setLoadingTmpl(true);
        const res = await getDesempenhoTarefasTemplates();
        if (res) setTemplates(res.data || res);
        setLoadingTmpl(false);
    };

    useEffect(() => { fetchDeps(); fetchTemplates(); }, []);

    const updateDep = async (dep, patch) => {
        const res = await updateDesempenhoDepartamento(dep.id, patch);
        if (res && (res.data || res.id)) {
            toast({ status: "success", title: "Configuração salva" });
            fetchDeps();
        } else {
            toast({ status: "error", title: "Erro ao salvar" });
        }
    };

    const openCreateTmpl = () => {
        setEditingTmpl(null);
        setTmplForm({ codigo: "", nome: "", tipo: "custom", descricao: "", peso: 10, departamento_filtro: "todos", modo: "poli", meta_diaria: "", config_json: "{}", calculador: "" });
        tmplOnOpen();
    };

    const openEditTmpl = (t) => {
        setEditingTmpl(t);
        let cfgObj = {};
        try { cfgObj = typeof t.config_json === "string" ? JSON.parse(t.config_json || "{}") : (t.config_json || {}); } catch { cfgObj = {}; }
        setTmplForm({
            codigo: t.codigo, nome: t.nome, tipo: t.tipo, descricao: t.descricao || "",
            peso: t.peso, departamento_filtro: t.departamento_filtro,
            modo: t.modo || "poli",
            meta_diaria: cfgObj.meta_diaria != null ? String(cfgObj.meta_diaria) : "",
            config_json: typeof t.config_json === "string" ? t.config_json : JSON.stringify(t.config_json || {}, null, 2),
            calculador: t.calculador || "", ativo: t.ativo,
        });
        tmplOnOpen();
    };

    const saveTmpl = async () => {
        let parsed;
        try { parsed = tmplForm.config_json ? JSON.parse(tmplForm.config_json) : {}; }
        catch { toast({ status: "error", title: "config_json inválido (não é JSON válido)" }); return; }

        // A "Meta diária" amigável sobrescreve config_json.meta_diaria.
        if (tmplForm.meta_diaria !== "" && tmplForm.meta_diaria != null) {
            parsed.meta_diaria = Number(tmplForm.meta_diaria);
        }

        const payload = { ...tmplForm, config_json: parsed, peso: Number(tmplForm.peso) };
        delete payload.meta_diaria;
        const res = editingTmpl
            ? await updateDesempenhoTarefaTemplate(editingTmpl.id, payload)
            : await createDesempenhoTarefaTemplate(payload);
        if (res && (res.data || res.id)) {
            toast({ status: "success", title: "Template salvo" });
            tmplOnClose();
            fetchTemplates();
        } else {
            const msg = res?.data?.message || res?.message || "Erro ao salvar";
            toast({ status: "error", title: msg });
        }
    };

    const delTmpl = async (t) => {
        const res = await deleteDesempenhoTarefaTemplate(t.id);
        if (res) { toast({ status: "success", title: "Template desativado" }); fetchTemplates(); }
        else toast({ status: "error", title: "Erro ao desativar" });
    };

    return (
        <Box>
            <Tabs variant="soft-rounded" colorScheme="orange" size="sm">
                <TabList mb={4}>
                    <Tab fontSize="sm"><Building2 size={14} style={{ marginRight: 6 }} /> Departamentos</Tab>
                    <Tab fontSize="sm"><Wrench size={14} style={{ marginRight: 6 }} /> Templates de Tarefa</Tab>
                </TabList>
                <TabPanels>
                    {/* DEPARTAMENTOS */}
                    <TabPanel px={0}>
                        {loadingDeps ? (
                            <Flex justify="center" py={8}><Spinner color="orange.500" /></Flex>
                        ) : (
                            <SimpleGrid columns={{ base: 1, md: 3 }} spacing={4}>
                                {deps.map(d => <DepartamentoCard key={d.id} dep={d} onSave={updateDep} />)}
                            </SimpleGrid>
                        )}
                    </TabPanel>

                    {/* TEMPLATES */}
                    <TabPanel px={0}>
                        <Flex justify="space-between" align="center" mb={3}>
                            <Text fontSize="xs" color="gray.500">{templates.length} template(s)</Text>
                            <Button size="sm" leftIcon={<Plus size={14} />} colorScheme="orange" onClick={openCreateTmpl}>Novo Template</Button>
                        </Flex>
                        <Box bg="white" borderRadius="xl" boxShadow="sm" border="1px solid" borderColor="gray.200" overflow="hidden">
                            {loadingTmpl ? (
                                <Flex justify="center" py={8}><Spinner color="orange.500" /></Flex>
                            ) : (
                                <Box overflowX="auto">
                                    <Table size="sm" variant="simple">
                                        <Thead bg="gray.50">
                                            <Tr>
                                                <Th fontSize="xs">Código</Th>
                                                <Th fontSize="xs">Nome</Th>
                                                <Th fontSize="xs">Tipo</Th>
                                                <Th fontSize="xs" isNumeric>Peso</Th>
                                                <Th fontSize="xs">Depto</Th>
                                                <Th fontSize="xs">Status</Th>
                                                <Th fontSize="xs" textAlign="center">Ações</Th>
                                            </Tr>
                                        </Thead>
                                        <Tbody>
                                            {templates.map(t => {
                                                const dep = DEP_BADGE[t.departamento_filtro] || { label: t.departamento_filtro, color: "gray" };
                                                return (
                                                    <Tr key={t.id} _hover={{ bg: "gray.50" }}>
                                                        <Td fontSize="xs" color="gray.600" fontFamily="mono">{t.codigo}</Td>
                                                        <Td fontSize="sm" fontWeight="semibold">{t.nome}</Td>
                                                        <Td><Badge colorScheme="cyan" fontSize="10px">{t.tipo}</Badge></Td>
                                                        <Td isNumeric><Text fontSize="xs" fontWeight="bold">{t.peso}</Text></Td>
                                                        <Td><HStack spacing={1}><Badge colorScheme={dep.color} fontSize="10px">{dep.label}</Badge>{t.modo === "crm" && <Badge colorScheme="purple" fontSize="9px">CRM</Badge>}</HStack></Td>
                                                        <Td><Badge colorScheme={t.ativo ? "green" : "gray"} fontSize="10px">{t.ativo ? "Ativo" : "Inativo"}</Badge></Td>
                                                        <Td textAlign="center">
                                                            <HStack spacing={1} justify="center">
                                                                <IconButton icon={<Pencil size={13} />} size="xs" variant="ghost" colorScheme="orange" aria-label="editar" onClick={() => openEditTmpl(t)} />
                                                                <IconButton icon={<Trash2 size={13} />} size="xs" variant="ghost" colorScheme="red" aria-label="delete" onClick={() => delTmpl(t)} isDisabled={!t.ativo} />
                                                            </HStack>
                                                        </Td>
                                                    </Tr>
                                                );
                                            })}
                                        </Tbody>
                                    </Table>
                                </Box>
                            )}
                        </Box>

                        {/* Modal Template */}
                        <Modal isOpen={tmplOpen} onClose={tmplOnClose} size="xl" isCentered>
                            <ModalOverlay />
                            <ModalContent borderRadius="xl">
                                <ModalHeader fontSize="md" pb={2}>{editingTmpl ? "Editar Template" : "Novo Template"}</ModalHeader>
                                <ModalCloseButton />
                                <ModalBody pb={4}>
                                    <SimpleGrid columns={[1, 2]} spacing={4}>
                                        <FormControl isRequired isDisabled={!!editingTmpl}>
                                            <FormLabel fontSize="xs" fontWeight="semibold" color="gray.600">Código (slug)</FormLabel>
                                            <Input size="sm" borderRadius="md" value={tmplForm.codigo} onChange={(e) => setTmplForm(f => ({ ...f, codigo: e.target.value }))} placeholder="ex: atendimentos_por_hora" />
                                        </FormControl>
                                        <FormControl isRequired>
                                            <FormLabel fontSize="xs" fontWeight="semibold" color="gray.600">Nome</FormLabel>
                                            <Input size="sm" borderRadius="md" value={tmplForm.nome} onChange={(e) => setTmplForm(f => ({ ...f, nome: e.target.value }))} />
                                        </FormControl>
                                        <FormControl isRequired isDisabled={!!editingTmpl}>
                                            <FormLabel fontSize="xs" fontWeight="semibold" color="gray.600">Tipo</FormLabel>
                                            <Select size="sm" borderRadius="md" value={tmplForm.tipo} onChange={(e) => setTmplForm(f => ({ ...f, tipo: e.target.value }))}>
                                                <option value="sla_resposta">sla_resposta</option>
                                                <option value="avaliacao_ia">avaliacao_ia</option>
                                                <option value="perguntas_ml_respondidas">perguntas_ml_respondidas</option>
                                                <option value="custom">custom</option>
                                            </Select>
                                        </FormControl>
                                        <FormControl isRequired>
                                            <FormLabel fontSize="xs" fontWeight="semibold" color="gray.600">Peso (1–100)</FormLabel>
                                            <NumberInput size="sm" min={1} max={100} value={tmplForm.peso} onChange={(v) => setTmplForm(f => ({ ...f, peso: v }))}>
                                                <NumberInputField borderRadius="md" />
                                            </NumberInput>
                                        </FormControl>
                                        <FormControl>
                                            <FormLabel fontSize="xs" fontWeight="semibold" color="gray.600">Departamento Filtro</FormLabel>
                                            <Select size="sm" borderRadius="md" value={tmplForm.departamento_filtro} onChange={(e) => setTmplForm(f => ({ ...f, departamento_filtro: e.target.value }))}>
                                                <option value="todos">Todos</option>
                                                <option value="comercial">Comercial</option>
                                                <option value="suporte">Suporte</option>
                                                <option value="financeiro">Financeiro</option>
                                            </Select>
                                        </FormControl>
                                        <FormControl>
                                            <FormLabel fontSize="xs" fontWeight="semibold" color="gray.600">Modo</FormLabel>
                                            <Select size="sm" borderRadius="md" value={tmplForm.modo} onChange={(e) => setTmplForm(f => ({ ...f, modo: e.target.value }))}>
                                                <option value="poli">POLI (atendimento)</option>
                                                <option value="crm">CRM (gerente de contas)</option>
                                            </Select>
                                        </FormControl>
                                        <FormControl>
                                            <FormLabel fontSize="xs" fontWeight="semibold" color="gray.600">Meta diária</FormLabel>
                                            <NumberInput size="sm" min={0} value={tmplForm.meta_diaria} onChange={(v) => setTmplForm(f => ({ ...f, meta_diaria: v }))}>
                                                <NumberInputField borderRadius="md" placeholder="ex: 20" />
                                            </NumberInput>
                                            <Text fontSize="10px" color="gray.400" mt={1}>Alvo do dia (grava em config_json.meta_diaria). Deixe vazio se o indicador não usa meta.</Text>
                                        </FormControl>
                                        <FormControl>
                                            <FormLabel fontSize="xs" fontWeight="semibold" color="gray.600">Calculador (custom)</FormLabel>
                                            <Input size="sm" borderRadius="md" value={tmplForm.calculador} onChange={(e) => setTmplForm(f => ({ ...f, calculador: e.target.value }))} placeholder="App\Services\CustomCalculators@metodo" />
                                        </FormControl>
                                        <FormControl gridColumn={["auto", "span 2"]}>
                                            <FormLabel fontSize="xs" fontWeight="semibold" color="gray.600">Descrição</FormLabel>
                                            <Textarea size="sm" borderRadius="md" rows={2} value={tmplForm.descricao} onChange={(e) => setTmplForm(f => ({ ...f, descricao: e.target.value }))} />
                                        </FormControl>
                                        <FormControl gridColumn={["auto", "span 2"]}>
                                            <FormLabel fontSize="xs" fontWeight="semibold" color="gray.600">config_json (JSON)</FormLabel>
                                            <Textarea size="sm" fontFamily="mono" fontSize="xs" rows={4} borderRadius="md" value={tmplForm.config_json} onChange={(e) => setTmplForm(f => ({ ...f, config_json: e.target.value }))} />
                                        </FormControl>
                                    </SimpleGrid>
                                </ModalBody>
                                <ModalFooter pt={0}>
                                    <Button size="sm" variant="ghost" mr={2} onClick={tmplOnClose}>Cancelar</Button>
                                    <Button size="sm" colorScheme="orange" onClick={saveTmpl}>{editingTmpl ? "Salvar" : "Criar"}</Button>
                                </ModalFooter>
                            </ModalContent>
                        </Modal>
                    </TabPanel>
                </TabPanels>
            </Tabs>
        </Box>
    );
};

const DepartamentoCard = ({ dep, onSave }) => {
    const [local, setLocal] = useState({
        sla_minutos: dep.sla_minutos,
        pct_avaliacao_diaria: dep.pct_avaliacao_diaria,
        avaliacao_ia_ativa: !!dep.avaliacao_ia_ativa,
        hora_cron: dep.hora_cron,
        ativo: !!dep.ativo,
    });
    const dirty = JSON.stringify(local) !== JSON.stringify({
        sla_minutos: dep.sla_minutos, pct_avaliacao_diaria: dep.pct_avaliacao_diaria,
        avaliacao_ia_ativa: !!dep.avaliacao_ia_ativa, hora_cron: dep.hora_cron, ativo: !!dep.ativo,
    });
    const dBadge = DEP_BADGE[dep.nome?.toLowerCase()] || { label: dep.nome, color: "gray" };

    return (
        <Box bg="white" border="1px solid" borderColor="gray.200" borderRadius="xl" p={4} boxShadow="sm">
            <Flex justify="space-between" align="center" mb={3}>
                <HStack>
                    <Badge colorScheme={dBadge.color} fontSize="11px" borderRadius="md" px={2}>{dep.nome}</Badge>
                    <Text fontSize="10px" color="gray.400">ID: {dep.departamento_id_poli}</Text>
                </HStack>
                <Switch colorScheme="orange" isChecked={local.ativo} onChange={(e) => setLocal(l => ({ ...l, ativo: e.target.checked }))} />
            </Flex>
            <VStack spacing={3} align="stretch">
                <FormControl>
                    <FormLabel fontSize="10px" color="gray.500" fontWeight="semibold" mb={1}>SLA (minutos)</FormLabel>
                    <NumberInput size="sm" min={1} max={1440} value={local.sla_minutos} onChange={(v) => setLocal(l => ({ ...l, sla_minutos: Number(v) }))}>
                        <NumberInputField borderRadius="md" />
                    </NumberInput>
                </FormControl>
                <FormControl>
                    <FormLabel fontSize="10px" color="gray.500" fontWeight="semibold" mb={1}>% Avaliação Diária IA</FormLabel>
                    <NumberInput size="sm" min={0} max={100} value={local.pct_avaliacao_diaria} onChange={(v) => setLocal(l => ({ ...l, pct_avaliacao_diaria: Number(v) }))}>
                        <NumberInputField borderRadius="md" />
                    </NumberInput>
                </FormControl>
                <FormControl>
                    <FormLabel fontSize="10px" color="gray.500" fontWeight="semibold" mb={1}>Hora do Cron (0–23)</FormLabel>
                    <NumberInput size="sm" min={0} max={23} value={local.hora_cron} onChange={(v) => setLocal(l => ({ ...l, hora_cron: Number(v) }))}>
                        <NumberInputField borderRadius="md" />
                    </NumberInput>
                </FormControl>
                <FormControl display="flex" alignItems="center" justifyContent="space-between">
                    <FormLabel fontSize="xs" color="gray.600" fontWeight="semibold" mb={0}>Avaliação IA ativa</FormLabel>
                    <Switch colorScheme="purple" isChecked={local.avaliacao_ia_ativa} onChange={(e) => setLocal(l => ({ ...l, avaliacao_ia_ativa: e.target.checked }))} />
                </FormControl>
                <Button size="xs" colorScheme="orange" isDisabled={!dirty} onClick={() => onSave(dep, local)}>Salvar Alterações</Button>
            </VStack>
        </Box>
    );
};

// ===== SYNC MODAL =====
const SyncManualModal = ({ isOpen, onClose }) => {
    const toast = useToast();
    const [data, setData] = useState(ontemISO());
    const [atendenteId, setAtendenteId] = useState("");
    const [departamento, setDepartamento] = useState("");
    const [skipIa, setSkipIa] = useState(false);
    const [running, setRunning] = useState(false);
    const [resumo, setResumo] = useState(null);
    const [atendentes, setAtendentes] = useState([]);

    useEffect(() => {
        if (!isOpen) return;
        (async () => {
            const res = await getDesempenhoAtendentes({ ativo: 1 });
            if (res && res.data) setAtendentes(res.data.filter(a => !a.user?.bloqueado));
        })();
    }, [isOpen]);

    const run = async () => {
        setRunning(true); setResumo(null);
        const payload = { data };
        if (atendenteId) payload.atendente_id = Number(atendenteId);
        if (departamento) payload.departamento = departamento;
        if (skipIa) payload.skip_ia = true;

        const res = await sincronizarDesempenho(payload);
        if (res) {
            toast({ status: "success", title: "Sincronização concluída" });
            setResumo(res.resumo || res.data?.resumo || res);
        } else {
            toast({ status: "error", title: "Erro ao sincronizar" });
        }
        setRunning(false);
    };

    const handleClose = () => {
        if (running) return;
        setResumo(null);
        onClose();
    };

    return (
        <Modal isOpen={isOpen} onClose={handleClose} size="xl" isCentered closeOnOverlayClick={!running}>
            <ModalOverlay />
            <ModalContent borderRadius="xl">
                <ModalHeader fontSize="md" pb={2}>
                    <HStack><PlayCircle size={18} color="#F68633" /><Text>Sincronização Manual</Text></HStack>
                </ModalHeader>
                <ModalCloseButton isDisabled={running} />
                <ModalBody pb={4}>
                    <Text fontSize="xs" color="gray.500" mb={3}>
                        Equivalente ao cron diário (03:00). Pode demorar conforme o volume (30–60 min em dias cheios).
                    </Text>
                    <SimpleGrid columns={[1, 2]} spacing={3}>
                        <FormControl>
                            <FormLabel fontSize="xs" fontWeight="semibold" color="gray.600">Data</FormLabel>
                            <Input size="sm" type="date" borderRadius="md" value={data} onChange={(e) => setData(e.target.value)} isDisabled={running} />
                        </FormControl>
                        <FormControl>
                            <FormLabel fontSize="xs" fontWeight="semibold" color="gray.600">Departamento</FormLabel>
                            <Select size="sm" borderRadius="md" value={departamento} onChange={(e) => setDepartamento(e.target.value)} isDisabled={running}>
                                <option value="">Todos</option>
                                <option value="comercial">Comercial</option>
                                <option value="suporte">Suporte</option>
                                <option value="financeiro">Financeiro</option>
                            </Select>
                        </FormControl>
                        <FormControl gridColumn={["auto", "span 2"]}>
                            <FormLabel fontSize="xs" fontWeight="semibold" color="gray.600">Atendente (opcional)</FormLabel>
                            <Select size="sm" borderRadius="md" value={atendenteId} onChange={(e) => setAtendenteId(e.target.value)} isDisabled={running}>
                                <option value="">Todos atendentes</option>
                                {atendentes.map(a => <option key={a.id} value={a.id}>{a.user?.name || a.apelido_poli}</option>)}
                            </Select>
                        </FormControl>
                        <FormControl display="flex" alignItems="center">
                            <Switch colorScheme="orange" isChecked={skipIa} onChange={(e) => setSkipIa(e.target.checked)} isDisabled={running} />
                            <FormLabel ml={2} mb={0} fontSize="xs" fontWeight="semibold" color="gray.600">Pular avaliação IA (mais rápido)</FormLabel>
                        </FormControl>
                    </SimpleGrid>

                    {running && (
                        <Box mt={4}>
                            <Progress size="xs" isIndeterminate colorScheme="orange" borderRadius="md" />
                            <Text fontSize="xs" color="gray.500" mt={2} textAlign="center">Processando... isso pode levar vários minutos.</Text>
                        </Box>
                    )}

                    {resumo && (
                        <Box mt={4} bg="gray.50" border="1px solid" borderColor="gray.200" borderRadius="md" p={3}>
                            <Text fontSize="xs" color="gray.500" fontWeight="bold" mb={2} textTransform="uppercase">Resumo</Text>
                            <pre style={{ fontSize: 11, whiteSpace: "pre-wrap", color: "#334155", margin: 0 }}>
                                {JSON.stringify(resumo, null, 2)}
                            </pre>
                        </Box>
                    )}
                </ModalBody>
                <ModalFooter pt={0}>
                    <Button size="sm" variant="ghost" mr={2} onClick={handleClose} isDisabled={running}>Fechar</Button>
                    <Button size="sm" colorScheme="orange" leftIcon={<PlayCircle size={14} />} onClick={run} isLoading={running} loadingText="Sincronizando...">Executar</Button>
                </ModalFooter>
            </ModalContent>
        </Modal>
    );
};

// ===== RAMAL INTERNO PRESENCA REAL-TIME MODAL =====
const RamalInternoPresencaModal = ({ isOpen, onClose }) => {
    const toast = useToast();
    const [loading, setLoading] = useState(false);
    const [resultado, setResultado] = useState(null);
    const [userId, setUserId] = useState("");

    const consultar = useCallback(async () => {
        setLoading(true);
        const res = await getDesempenhoRamalInternoPresencaRealtime();
        if (res && res.data) {
            setResultado(res.data);
        } else {
            const msg = res?.error || "Falha ao consultar presença";
            toast({ status: "error", title: msg, description: res?.detalhes });
            setResultado(null);
        }
        setLoading(false);
    }, [toast]);

    useEffect(() => {
        if (isOpen) {
            setUserId("");
            consultar();
        } else {
            setResultado(null);
        }
    }, [isOpen, consultar]);

    const statusBadge = (status) => {
        if (status === "Online") return <Badge colorScheme="green" fontSize="11px" borderRadius="md" px={2}>Online</Badge>;
        if (status === "Offline") return <Badge colorScheme="red" fontSize="11px" borderRadius="md" px={2}>Offline</Badge>;
        return <Badge colorScheme="yellow" fontSize="11px" borderRadius="md" px={2}>{status || "—"}</Badge>;
    };

    const consultadoEmLabel = resultado?.consultado_em
        ? new Date(resultado.consultado_em).toLocaleString("pt-BR")
        : null;

    const atendentes = resultado?.atendentes || [];
    const selecionado = userId
        ? atendentes.find(a => String(a.user_id) === String(userId))
        : null;

    const formatLastSeen = (a) => {
        if (!a?.last_seen) return "Nunca conectou";
        const s = Number(a.last_seen_segundos ?? 0);
        if (s < 60) return `há ${s}s`;
        if (s < 3600) return `há ${Math.floor(s / 60)} min`;
        if (s < 86400) return `há ${Math.floor(s / 3600)}h`;
        return `há ${Math.floor(s / 86400)}d`;
    };

    return (
        <Modal isOpen={isOpen} onClose={onClose} size="2xl" isCentered scrollBehavior="inside">
            <ModalOverlay />
            <ModalContent borderRadius="xl">
                <ModalHeader fontSize="md" pb={2}>
                    <HStack><Phone size={18} color="#3b82f6" /><Text>Presença no Ramal Interno</Text></HStack>
                </ModalHeader>
                <ModalCloseButton />
                <ModalBody pb={4}>
                    {consultadoEmLabel && (
                        <Text fontSize="xs" color="gray.500" mb={3}>
                            Consultado em: {consultadoEmLabel}
                            {resultado?.tolerancia_seg ? ` · tolerância ${resultado.tolerancia_seg}s` : ""}
                        </Text>
                    )}

                    {loading ? (
                        <Box py={6}>
                            <Progress size="xs" isIndeterminate colorScheme="blue" borderRadius="md" />
                            <Text fontSize="xs" color="gray.500" mt={3} textAlign="center">Consultando ramal interno...</Text>
                        </Box>
                    ) : !resultado ? (
                        <Box py={6} textAlign="center">
                            <Text fontSize="sm" color="gray.500">Sem dados.</Text>
                        </Box>
                    ) : (
                        <>
                            <SimpleGrid columns={3} spacing={3} mb={4}>
                                <Box bg="gray.50" borderRadius="md" p={3} textAlign="center">
                                    <Text fontSize="10px" color="gray.500" fontWeight="semibold" textTransform="uppercase">Total</Text>
                                    <Text fontSize="xl" fontWeight="bold" color="gray.800">{resultado.total}</Text>
                                </Box>
                                <Box bg="green.50" borderRadius="md" p={3} textAlign="center">
                                    <Text fontSize="10px" color="green.600" fontWeight="semibold" textTransform="uppercase">Online</Text>
                                    <Text fontSize="xl" fontWeight="bold" color="green.700">{resultado.online}</Text>
                                </Box>
                                <Box bg="red.50" borderRadius="md" p={3} textAlign="center">
                                    <Text fontSize="10px" color="red.600" fontWeight="semibold" textTransform="uppercase">Offline</Text>
                                    <Text fontSize="xl" fontWeight="bold" color="red.700">{resultado.offline}</Text>
                                </Box>
                            </SimpleGrid>

                            <Box mb={4}>
                                <Text fontSize="10px" color="gray.500" fontWeight="semibold" textTransform="uppercase" mb={1}>
                                    Selecione um usuário
                                </Text>
                                <Select
                                    size="sm"
                                    bg="white"
                                    borderRadius="md"
                                    placeholder="— Todos —"
                                    value={userId}
                                    onChange={(e) => setUserId(e.target.value)}
                                    isDisabled={atendentes.length === 0}
                                >
                                    {atendentes.map(a => (
                                        <option key={a.user_id ?? a.atendente_id} value={a.user_id ?? ""}>
                                            {a.nome} · ramal {a.nvoip_numbersip}
                                        </option>
                                    ))}
                                </Select>
                            </Box>

                            {selecionado ? (
                                <Box bg="gray.50" border="1px solid" borderColor="gray.200" borderRadius="md" p={4} mb={3}>
                                    <HStack spacing={4} align="flex-start">
                                        <Avatar size="md" name={selecionado.nome} />
                                        <Box flex={1} minW={0}>
                                            <Text fontSize="md" fontWeight="bold" color="gray.800" noOfLines={1}>{selecionado.nome}</Text>
                                            <HStack mt={1} spacing={2}>
                                                <Badge colorScheme={(DEP_BADGE[selecionado.departamento] || { color: "gray" }).color} fontSize="10px" borderRadius="md" px={2}>
                                                    {DEP_BADGE[selecionado.departamento]?.label || selecionado.departamento}
                                                </Badge>
                                                <Text fontSize="xs" color="gray.600" fontFamily="mono">ramal {selecionado.nvoip_numbersip}</Text>
                                            </HStack>
                                            <HStack mt={3} spacing={3}>
                                                {statusBadge(selecionado.status)}
                                                <Text fontSize="xs" color="gray.600">
                                                    Última atividade: <b>{formatLastSeen(selecionado)}</b>
                                                </Text>
                                            </HStack>
                                        </Box>
                                    </HStack>
                                </Box>
                            ) : atendentes.length > 0 ? (
                                <Box border="1px solid" borderColor="gray.200" borderRadius="md" overflow="hidden">
                                    <Table size="sm" variant="simple">
                                        <Thead bg="gray.50">
                                            <Tr>
                                                <Th fontSize="xs" color="gray.500">Atendente</Th>
                                                <Th fontSize="xs" color="gray.500">Depto</Th>
                                                <Th fontSize="xs" color="gray.500">Ramal</Th>
                                                <Th fontSize="xs" color="gray.500">Última atividade</Th>
                                                <Th fontSize="xs" color="gray.500">Status</Th>
                                            </Tr>
                                        </Thead>
                                        <Tbody>
                                            {atendentes.map(a => {
                                                const dep = DEP_BADGE[a.departamento] || { label: a.departamento, color: "gray" };
                                                return (
                                                    <Tr key={a.atendente_id} _hover={{ bg: "gray.50", cursor: "pointer" }} onClick={() => setUserId(String(a.user_id ?? ""))}>
                                                        <Td>
                                                            <HStack>
                                                                <Avatar size="xs" name={a.nome} />
                                                                <Text fontSize="sm" fontWeight="semibold" color="gray.800" noOfLines={1}>{a.nome}</Text>
                                                            </HStack>
                                                        </Td>
                                                        <Td><Badge colorScheme={dep.color} fontSize="10px" borderRadius="md" px={2}>{dep.label}</Badge></Td>
                                                        <Td><Text fontSize="xs" color="gray.700" fontFamily="mono">{a.nvoip_numbersip}</Text></Td>
                                                        <Td><Text fontSize="xs" color="gray.600">{formatLastSeen(a)}</Text></Td>
                                                        <Td>{statusBadge(a.status)}</Td>
                                                    </Tr>
                                                );
                                            })}
                                        </Tbody>
                                    </Table>
                                </Box>
                            ) : (
                                <Box py={4} textAlign="center">
                                    <Text fontSize="sm" color="gray.500">Nenhum atendente com ramal configurado.</Text>
                                </Box>
                            )}

                            {resultado.sem_numbersip?.length > 0 && (
                                <Box mt={4} bg="yellow.50" border="1px solid" borderColor="yellow.200" borderRadius="md" p={3}>
                                    <HStack mb={2}>
                                        <AlertCircle size={14} color="#b45309" />
                                        <Text fontSize="xs" color="yellow.800" fontWeight="bold">
                                            {resultado.sem_numbersip.length} atendente(s) ativo(s) sem ramal cadastrado
                                        </Text>
                                    </HStack>
                                    <VStack align="stretch" spacing={1}>
                                        {resultado.sem_numbersip.map(a => (
                                            <Text key={a.atendente_id} fontSize="xs" color="yellow.900">
                                                • {a.nome} ({DEP_BADGE[a.departamento]?.label || a.departamento})
                                            </Text>
                                        ))}
                                    </VStack>
                                </Box>
                            )}
                        </>
                    )}
                </ModalBody>
                <ModalFooter pt={0}>
                    <Button size="sm" variant="ghost" mr={2} onClick={onClose}>Fechar</Button>
                    <Button size="sm" colorScheme="blue" leftIcon={<RefreshCw size={14} />} onClick={consultar} isLoading={loading} loadingText="Consultando...">Atualizar</Button>
                </ModalFooter>
            </ModalContent>
        </Modal>
    );
};

// ===== MAIN PAGE =====
const TAB_KEYS = ["dashboard", "atendentes", "avaliacoes", "configuracoes"];

const DesempenhoAtendentes = () => {
    const { isOpen: syncOpen, onOpen: onSyncOpen, onClose: onSyncClose } = useDisclosure();
    const { isOpen: ramalOpen, onOpen: onRamalOpen, onClose: onRamalClose } = useDisclosure();
    const location = useLocation();
    const navigate = useNavigate();

    const params = useMemo(() => new URLSearchParams(location.search), [location.search]);
    const tabKey = params.get("tab");
    const initialAtendenteId = params.get("atendente_id") || "";
    const tabIndex = Math.max(0, TAB_KEYS.indexOf(tabKey));

    const handleTabChange = (idx) => {
        const next = new URLSearchParams(location.search);
        next.set("tab", TAB_KEYS[idx]);
        if (TAB_KEYS[idx] !== "avaliacoes") next.delete("atendente_id");
        navigate({ pathname: location.pathname, search: `?${next.toString()}` }, { replace: true });
    };

    return (
        <ContentShell>
            <Flex justify="space-between" align="center" mb={5} flexWrap="wrap" gap={3}>
                <Box>
                    <HStack>
                        <Trophy size={24} color="#F68633" />
                        <Text fontSize="xl" fontWeight="bold" color="gray.800">Desempenho de Atendentes</Text>
                    </HStack>
                    <Text fontSize="xs" color="gray.500" mt={1}>
                        SLA, avaliação IA por Claude Opus e ranking por departamento — cron diário às 03:00.
                    </Text>
                </Box>
            </Flex>

            <Box bg="white" borderRadius="xl" boxShadow="sm" border="1px solid" borderColor="gray.200" overflow="hidden">
                <Tabs variant="line" colorScheme="orange" size="md" isLazy index={tabIndex} onChange={handleTabChange}>
                    <TabList borderBottomWidth="1px" borderColor="gray.200" px={{ base: 2, md: 4 }} overflowX="auto" overflowY="hidden">
                        <Tab fontSize="sm" whiteSpace="nowrap"><BarChart3 size={14} style={{ marginRight: 6 }} />Dashboard</Tab>
                        <Tab fontSize="sm" whiteSpace="nowrap"><Users size={14} style={{ marginRight: 6 }} />Atendentes</Tab>
                        <Tab fontSize="sm" whiteSpace="nowrap"><Sparkles size={14} style={{ marginRight: 6 }} />Avaliações IA</Tab>
                        <Tab fontSize="sm" whiteSpace="nowrap"><Settings size={14} style={{ marginRight: 6 }} />Configurações</Tab>
                    </TabList>
                    <TabPanels>
                        <TabPanel p={{ base: 3, md: 5 }}><DashboardTab onOpenSync={onSyncOpen} onOpenRamal={onRamalOpen} /></TabPanel>
                        <TabPanel p={{ base: 3, md: 5 }}><AtendentesTab /></TabPanel>
                        <TabPanel p={{ base: 3, md: 5 }}><AvaliacoesTab initialAtendenteId={initialAtendenteId} /></TabPanel>
                        <TabPanel p={{ base: 3, md: 5 }}><ConfiguracoesTab /></TabPanel>
                    </TabPanels>
                </Tabs>
            </Box>

            <SyncManualModal isOpen={syncOpen} onClose={onSyncClose} />
            <RamalInternoPresencaModal isOpen={ramalOpen} onClose={onRamalClose} />
        </ContentShell>
    );
};

export default DesempenhoAtendentes;
